import React from "react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Star, Building2, GripVertical } from "lucide-react";
import { cn } from "@/lib/utils";

const COLUMNS = [
  { id: "prospect", label: "Prospekt", dot: "bg-slate-400" },
  { id: "initial_contact", label: "Innledende kontakt", dot: "bg-sky-400" },
  { id: "interested", label: "Noe interessert", dot: "bg-cyan-400" },
  { id: "info_sent", label: "Sendt info", dot: "bg-brand-limeDark" },
  { id: "evaluation", label: "Evaluering", dot: "bg-brand-limeDark" },
  { id: "letter_of_intent", label: "Intensjonsavtale", dot: "bg-amber-400" },
  { id: "negotiation_dd", label: "Forhandling/DD", dot: "bg-orange-400" },
  { id: "closed_won", label: "Gjennomført", dot: "bg-emerald-500" },
];

const fmtM = (n) => `${((n || 0) / 1e6).toFixed(1)}M`;
const scoreColor = (s) => (s >= 80 ? "text-emerald-600" : s >= 60 ? "text-amber-600" : "text-rose-600");

export default function CandidatePipeline({ candidates, onStatusChange, onOpen }) {
  const byStatus = COLUMNS.reduce((acc, c) => {
    acc[c.id] = candidates.filter((k) => (k.status || "prospect") === c.id);
    return acc;
  }, {});

  const handleDragEnd = (result) => {
    const { source, destination, draggableId } = result;
    if (!destination) return;
    if (destination.droppableId === source.droppableId) return;
    onStatusChange(draggableId, destination.droppableId);
  };

  return (
    <DragDropContext onDragEnd={handleDragEnd}>
      <div className="flex gap-4 overflow-x-auto pb-4 -mx-1 px-1">
        {COLUMNS.map((column) => {
          const items = byStatus[column.id] || [];
          const total = items.reduce((s, k) => s + (k.discussed_price || 0), 0);
          return (
            <div key={column.id} className="w-[290px] shrink-0">
              <div className="flex items-center justify-between px-3 py-2.5 mb-2 rounded-xl bg-white border border-slate-200/70 shadow-sm">
                <div className="flex items-center gap-2 min-w-0">
                  <span className={cn("w-2.5 h-2.5 rounded-full shrink-0", column.dot)} />
                  <span className="text-sm font-semibold text-brand-ink truncate">{column.label}</span>
                  <span className="text-xs font-semibold text-slate-400 shrink-0">{items.length}</span>
                </div>
                {total > 0 && <span className="text-xs text-slate-400 shrink-0">{fmtM(total)} kr</span>}
              </div>

              <Droppable droppableId={column.id}>
                {(provided, snapshot) => (
                  <div
                    ref={provided.innerRef}
                    {...provided.droppableProps}
                    className={cn(
                      "min-h-[120px] rounded-xl p-2 space-y-2 transition-colors duration-200",
                      snapshot.isDraggingOver ? "bg-brand-limeSoft/60" : "bg-slate-100/60"
                    )}
                  >
                    {items.map((candidate, index) => (
                      <Draggable key={candidate.id} draggableId={candidate.id} index={index}>
                        {(prov, snap) => (
                          <div
                            ref={prov.innerRef}
                            {...prov.draggableProps}
                            {...prov.dragHandleProps}
                            onClick={() => onOpen && onOpen(candidate)}
                            className={cn(
                              "group bg-white rounded-xl border border-slate-200/70 p-3.5 cursor-pointer transition-shadow",
                              snap.isDragging ? "shadow-lg ring-1 ring-brand-limeDark/40" : "shadow-sm hover:shadow-md"
                            )}
                          >
                            <div className="flex items-start gap-2">
                              <div className="w-8 h-8 rounded-lg bg-brand-limeSoft flex items-center justify-center shrink-0">
                                <Building2 className="w-4 h-4 text-brand-limeDark" />
                              </div>
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold text-brand-ink leading-tight truncate">{candidate.company_name}</p>
                                {candidate.contact_person && (
                                  <p className="text-xs text-slate-400 truncate">{candidate.contact_person}</p>
                                )}
                              </div>
                              <GripVertical className="w-4 h-4 text-slate-300 group-hover:text-slate-400 shrink-0" />
                            </div>
                            <div className="mt-3 flex items-center justify-between">
                              {candidate.discussed_price ? (
                                <span className="text-xs font-semibold text-brand-ink">{fmtM(candidate.discussed_price)} kr</span>
                              ) : <span className="text-xs text-slate-300">—</span>}
                              {candidate.overall_score ? (
                                <span className={cn("flex items-center gap-1 text-xs font-semibold", scoreColor(candidate.overall_score))}>
                                  <Star className="w-3.5 h-3.5" /> {candidate.overall_score}
                                </span>
                              ) : null}
                            </div>
                          </div>
                        )}
                      </Draggable>
                    ))}
                    {provided.placeholder}
                    {items.length === 0 && !snapshot.isDraggingOver && (
                      <p className="text-center text-xs text-slate-300 py-6">Dra kandidater hit</p>
                    )}
                  </div>
                )}
              </Droppable>
            </div>
          );
        })}
      </div>
    </DragDropContext>
  );
}
